import type { DialogStep, SpeakerId, SoundId } from '~/types/interfaces'

type KeyMode = 'unlock' | 'gift'

/**
 * ダイアログを閉じたときの結果（#393 / R6 で `string` から型安全化）。
 * - `submit`: 主アクション確定
 * - `cancel`: キャンセル / 閉じる
 * - `middle`: EventDialog の 3 択中央ボタン
 */
export type DialogResult = 'submit' | 'cancel' | 'middle'

interface ShowDialogState {
  visible: boolean
  params?: DialogStep
  sceneId?: string
  projectId?: string
  keyMode?: KeyMode
}

interface ShowDialogParams {
  params?: DialogStep
  sceneId?: string
  projectId?: string
  keyMode?: KeyMode
  resolve?: (result: DialogResult) => void
}

type DialogType = 'event' | 'name' | 'broker' | 'key' | 'video' | 'remixSource' | 'crystallizePreview' | 'crystallize' | 'consult' | 'assist' | 'restoreDraft'

/**
 * ダイアログ close 時に解決する Promise の resolve 関数群（#393 / R6）。
 *
 * resolve 関数は非シリアライズ・非リアクティブなので **Pinia state には置かず**、
 * モジュールスコープの Map で保持する（「resolve を store state に格納する」
 * アンチパターンの解消）。dialog type ごとに 1 つで、再表示で上書きされる
 * （従来 state 上の `_onClose` と同じ寿命）。
 */
const pendingResolvers = new Map<DialogType, (result: DialogResult) => void>()

// Expose dialog store on window for E2E testing (same pattern as __eventSystem)
let _e2eExposed = false
function exposeForE2E(store: ReturnType<typeof _useDialogStore>): void {
  if (_e2eExposed || !import.meta.client) return
  _e2eExposed = true
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(window as any).__dialogStore = {
    showDialog: (type: string, params: ShowDialogParams) => store.showDialog(type as DialogType, params),
    closeDialog: (type: string, result: string) => store.closeDialog(type as DialogType, result as DialogResult),
  }
}

const _useDialogStore = defineStore('dialog', {
  state: (): Record<DialogType, ShowDialogState> => ({
    event: { visible: false },
    name: {
      visible: false, params: {
        type: 'dialog' as const,
        speaker: 'system' as SpeakerId,
        title: '名前を変える',
        text: '',
        actions: {
          submit: {
            label: '決定する',
            disabled: false,
            sound: 'submit' as SoundId,
          },
          cancel: {
            label: 'とじる',
            disabled: false,
            sound: 'cancel' as SoundId,
          },
        },
      },
    },
    broker: {
      visible: false, params: {
        type: 'dialog' as const,
        speaker: 'system' as SpeakerId,
        title: '妖精との仲介者',
        text: '',
        actions: {
          cancel: {
            label: 'とじる',
            disabled: false,
            sound: 'cancel' as SoundId,
          },
        },
      },
    },
    key: {
      visible: false, params: {
        type: 'dialog' as const,
        speaker: 'system' as SpeakerId,
        title: 'キーを入力',
        text: '',
        actions: {
          submit: {
            label: '決定する',
            disabled: false,
            sound: 'submit' as SoundId,
          },
          cancel: {
            label: 'とじる',
            disabled: false,
            sound: 'cancel' as SoundId,
          },
        },
      },
    },
    video: {
      visible: false, params: {
        type: 'dialog' as const,
        speaker: 'system' as SpeakerId,
        title: '',
        text: '',
        actions: {
          cancel: {
            label: 'とじる',
            disabled: false,
            sound: 'cancel' as SoundId,
          },
        },
      },
    },
    remixSource: {
      visible: false, params: {
        type: 'dialog' as const,
        speaker: 'system' as SpeakerId,
        title: '魔導書を編む',
        text: '使う本と、使うページを選んでね。',
        actions: {
          cancel: {
            label: 'とじる',
            disabled: false,
            sound: 'cancel' as SoundId,
          },
        },
      },
    },
    crystallizePreview: {
      visible: false, params: {
        type: 'dialog' as const,
        speaker: 'system' as SpeakerId,
        title: '動作確認プレビュー',
        text: '',
        actions: {
          cancel: {
            label: 'キャンセル',
            disabled: false,
            sound: 'cancel' as SoundId,
          },
        },
      },
    },
    crystallize: {
      visible: false, params: {
        type: 'dialog' as const,
        speaker: 'system' as SpeakerId,
        title: 'クリスタル化',
        text: '',
        actions: {
          cancel: {
            label: 'とじる',
            disabled: false,
            sound: 'cancel' as SoundId,
          },
        },
      },
    },
    consult: {
      visible: false, params: {
        type: 'dialog' as const,
        speaker: 'system' as SpeakerId,
        title: 'フクロウ便',
        text: '',
        actions: {
          cancel: {
            label: 'とじる',
            disabled: false,
            sound: 'cancel' as SoundId,
          },
        },
      },
    },
    assist: {
      visible: false, params: {
        type: 'dialog' as const,
        speaker: 'system' as SpeakerId,
        title: 'AI支援',
        text: '',
        actions: {
          cancel: {
            label: 'とじる',
            disabled: false,
            sound: 'cancel' as SoundId,
          },
        },
      },
    },
    restoreDraft: {
      visible: false, params: {
        type: 'dialog' as const,
        speaker: 'system' as SpeakerId,
        title: '未保存の編集が残っています',
        text: '前回の保存からの変更がこのブラウザに残っています。復元するとそれを編集に戻します。破棄するとサーバー上の内容で開き直します。',
        actions: {
          submit: {
            label: '復元する',
            disabled: false,
            sound: 'submit' as SoundId,
          },
          cancel: {
            label: '破棄する',
            disabled: false,
            sound: 'cancel' as SoundId,
          },
        },
      },
    },
  }),
  actions: {
    showDialog(type: DialogType, { params, sceneId, projectId, keyMode, resolve }: ShowDialogParams): void {
      this[type].visible = true
      this[type].sceneId = sceneId
      this[type].projectId = projectId
      this[type].keyMode = keyMode
      if (params !== undefined) {
        this[type].params = params
      }
      if (resolve) {
        pendingResolvers.set(type, resolve)
      }
      else {
        pendingResolvers.delete(type)
      }
    },
    /**
     * ダイアログを表示し、閉じられるまで待つ Promise を返す（#393 / R6）。
     *
     * 呼び出し側に散在していた
     * `await new Promise(resolve => showDialog(type, { resolve }))` の定型を吸収し、
     * 結果を {@link DialogResult} として型安全に返す。fire-and-forget な表示は
     * 従来どおり {@link showDialog} を使う。
     */
    showDialogAsync(type: DialogType, params: Omit<ShowDialogParams, 'resolve'> = {}): Promise<DialogResult> {
      return new Promise<DialogResult>((resolve) => {
        this.showDialog(type, { ...params, resolve })
      })
    },
    closeDialog(type: DialogType, result: DialogResult): void {
      this[type].visible = false
      const resolve = pendingResolvers.get(type)
      pendingResolvers.delete(type)
      resolve?.(result)
    },
  },
})

export function useDialogStore() {
  const store = _useDialogStore()
  exposeForE2E(store)
  return store
}
